/**
 * CLASSIC LOOK (v1.70): the pre-Floodlight design from commit ea44aec, kept
 * for people/sites on the Classic UI style. Functionality must match the
 * Floodlight version (screens/CommunityScreen.jsx); port behaviour changes here too.
 *
 * Community — channels, live chat and shared bets.
 *
 * Layout by width:
 *   ≥ 1180  channels | conversation | members
 *   ≥ 760   channels | conversation, members in a slide-over
 *   < 760   conversation only; channels and members open as slide-overs
 * Members come from member cards (communities/main/members, written by the
 * communityJoin function), never from the users collection. Signed-out
 * visitors get a clear sign-in prompt instead of an input that fails.
 */
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { View, Text, StyleSheet, Platform, useWindowDimensions, Modal, Pressable } from "react-native";

import { useTicker } from "../../context/TickerContext";
import { useAuth } from "../../context/AuthContext";
import { useNavigation } from "../../context/NavigationContext";
import NavigationHeader from "../../components/NavigationHeader";
import ChannelsSidebar from "../../components/community/classic/ChannelsSidebar";
import MembersList from "../../components/community/classic/MembersList";
import ChannelContent from "../../components/community/classic/ChannelContent";
import { ScreenBackground } from "../../components/ThemeGradients";
import EdgeLine from "../../components/motion/EdgeLine";
import Appear from "../../components/motion/Appear";
import KineticHeadline from "../../components/motion/KineticHeadline";
import AmbientGlow from "../../components/motion/AmbientGlow";
import Spotlight from "../../components/motion/Spotlight";
import Reveal from "../../components/motion/Reveal";
import MagneticButton from "../../components/motion/MagneticButton";
import CursorAura from "../../components/motion/CursorAura";
import Grain from "../../components/motion/Grain";
import { Ionicons } from "@expo/vector-icons";
import { IS_WEB } from "../../utils/motion";
import injectCss from "../../utils/injectCss";

import {
  subscribeToChannels,
  subscribeToMemberPresence,
  initPresence,
  joinCommunityServer,
} from "../../services/communityService";

const COMMUNITY_ID = "main";

// What members get, shown to signed-out visitors (real features only).
const PERKS = [
  ["chatbubbles-outline", "Channels by sport", "Talk lines and news in rooms organized by sport and league."],
  ["share-social-outline", "Post from your slip", "Share a bet straight from your bet slip, priced and ready to read."],
  ["pulse-outline", "Today's price in one tap", "Re-run any shared bet in one tap to see what the number is now."],
];

// The online dot breathes; channel and member rows slide in with an accent
// bar, messages light their gutter, perks pop their icon (web; hover effects
// for fine pointers only; all still under reduced motion).
const PULSE_CSS = `
[data-online-dot] { animation: boOnlinePulse 2.4s ease-in-out infinite; }
@keyframes boOnlinePulse { 0%, 100% { box-shadow: 0 0 0 0 rgba(52,211,153,0.55); } 50% { box-shadow: 0 0 0 5px rgba(52,211,153,0); } }
/* Rows are TouchableOpacity, which writes an inline transition-duration: ours must win, opacity (the press fade) kept. */
[data-crow] { position: relative; transition-property: transform, background-color, opacity !important; transition-duration: 460ms, 200ms, 150ms !important; transition-timing-function: cubic-bezier(0.34,1.4,0.64,1), ease, ease !important; }
[data-crow]::before { content: ''; position: absolute; left: 0; top: 22%; bottom: 22%; width: 3px; border-radius: 2px; pointer-events: none;
  background: linear-gradient(180deg, #6366F1, #34D399); transform: scaleY(0); transition: transform 380ms cubic-bezier(0.16,1,0.3,1); }
[data-avatar] { transition: transform 460ms cubic-bezier(0.34,1.4,0.64,1), box-shadow 260ms; }
[data-msg] { border-radius: 10px; transition: background-color 220ms, box-shadow 220ms; }
[data-perk-icon] { transition: transform 560ms cubic-bezier(0.34,1.4,0.64,1), background-color 260ms; }
[data-perk-line] { transform: scaleX(0); transform-origin: left; transition: transform 760ms cubic-bezier(0.16,1,0.3,1); }
@media (hover: hover) and (pointer: fine) {
  [data-crow="idle"]:hover { transform: translateX(4px); }
  [data-crow="idle"]:hover::before { transform: scaleY(1); }
  [data-crow]:hover [data-avatar] { transform: scale(1.1); box-shadow: 0 0 0 2px rgba(52,211,153,0.5); }
  [data-msg]:hover { background-color: rgba(255,255,255,0.03); box-shadow: inset 2px 0 0 rgba(139,92,246,0.7); }
  [data-spotlight]:hover [data-perk-icon] { transform: translate3d(0, -4px, 0) rotate(-10deg) scale(1.12); background-color: rgba(52,211,153,0.16); }
  [data-spotlight]:hover [data-perk-line] { transform: scaleX(1); }
}
@media (prefers-reduced-motion: reduce) {
  [data-online-dot] { animation: none; }
  [data-crow], [data-crow]::before, [data-avatar], [data-perk-icon], [data-perk-line] { transition: none; }
  [data-crow="idle"]:hover, [data-crow]:hover [data-avatar], [data-spotlight]:hover [data-perk-icon] { transform: none; }
}
`;
const UI = Platform.OS === "web" ? 'Slate, -apple-system, system-ui, "Segoe UI", sans-serif' : undefined;

function findChannel(categories, id) {
  for (const cat of categories) {
    for (const ch of cat.channels ?? []) {
      if (ch.id === id) return { channel: ch, categoryId: cat.id };
      for (const sub of ch.subChannels ?? []) if (sub.id === id) return { channel: sub, categoryId: cat.id };
    }
  }
  return { channel: null, categoryId: null };
}

function SlideOver({ open, side = "left", onClose, children, label }) {
  if (!open) return null;
  return (
    <Modal visible transparent animationType="fade" onRequestClose={onClose}>
      <View style={[styles.overlay, { flexDirection: side === "left" ? "row" : "row-reverse" }]}>
        <Appear from={side === "left" ? "left" : "right"} distance={24} style={styles.sheet} accessibilityLabel={label}>
          {children}
        </Appear>
        <Pressable style={{ flex: 1 }} onPress={onClose} accessibilityRole="button" accessibilityLabel="Close panel" />
      </View>
    </Modal>
  );
}

export default function CommunityScreen() {
  if (IS_WEB) injectCss("online-pulse", PULSE_CSS);
  const { totalHeaderHeight } = useTicker();
  const { user } = useAuth();
  const { navigateTo } = useNavigation();
  const { width, height } = useWindowDimensions();
  const threeCol = width >= 1180;
  const twoCol = width >= 760;

  const [categories, setCategories] = useState([]);
  const [channelsLoading, setChannelsLoading] = useState(true);
  const [channelsError, setChannelsError] = useState(null);
  const [members, setMembers] = useState([]);
  const [activeChannelId, setActiveChannelId] = useState(null);
  const [channelsOpen, setChannelsOpen] = useState(false);
  const [membersOpen, setMembersOpen] = useState(false);

  useEffect(() => {
    if (activeChannelId || !categories.length) return;
    const first = categories[0]?.channels?.[0];
    if (first) setActiveChannelId(first.id);
  }, [categories, activeChannelId]);

  const { channel: activeChannel, categoryId: activeCategoryId } = useMemo(() => findChannel(categories, activeChannelId), [categories, activeChannelId]);
  const activeChannelName = activeChannel ? (activeChannel.displayName ?? activeChannel.name ?? activeChannel.id) : null;

  const displayMembers = useMemo(
    () => members.map((m) => (m.uid === user?.uid ? { ...m, onlineStatus: "online" } : m)),
    [members, user?.uid],
  );
  const onlineCount = displayMembers.filter((m) => m.onlineStatus === "online").length;

  useEffect(() => {
    if (!user?.uid) { setChannelsLoading(false); return undefined; }
    setChannelsLoading(true);
    const unsubChannels = subscribeToChannels(COMMUNITY_ID, (cats) => { setCategories(cats); setChannelsLoading(false); }, (e) => setChannelsError(e));
    const unsubMembers = subscribeToMemberPresence(COMMUNITY_ID, setMembers);
    return () => { unsubChannels(); unsubMembers(); };
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid) return undefined;
    let cleanup = () => {};
    let gone = false;
    // Member card first (the presence mirror only updates existing cards).
    joinCommunityServer(COMMUNITY_ID).finally(() => {
      if (gone) return;
      initPresence(COMMUNITY_ID, user.uid).then(({ cleanup: fn }) => { if (gone) fn(); else cleanup = fn; });
    });
    return () => { gone = true; cleanup(); };
  }, [user?.uid]);

  const selectChannel = useCallback((id) => { setActiveChannelId(id); setChannelsOpen(false); }, []);

  const paddingTop = Platform.OS === "web" ? totalHeaderHeight + (width >= 768 ? 22 : 12) : 0;
  const padX = width >= 1024 ? 24 : width >= 600 ? 16 : 8;

  const channels = (
    <ChannelsSidebar
      categories={categories}
      loading={channelsLoading}
      error={channelsError}
      selectedChannelId={activeChannelId}
      onChannelSelect={selectChannel}
      style={!twoCol ? { width: "100%", height: "100%", borderRadius: 0 } : undefined}
    />
  );
  const memberList = <MembersList members={displayMembers} style={!threeCol ? { width: "100%", height: "100%", borderRadius: 0 } : { width: 232 }} />;

  return (
    <View style={styles.root}>
      <ScreenBackground />
      <Grain />
      {!user && <CursorAura />}
      <NavigationHeader />
      <View style={[styles.body, { paddingTop, paddingHorizontal: padX }]}>
        <Appear style={[styles.intro, width < 600 && { paddingVertical: 10 }]}>
          <EdgeLine variant="flat" height={44} style={styles.introLine} opacity={0.7} />
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text style={[styles.introTitle, width < 600 && { fontSize: 18 }]} accessibilityRole="header" aria-level={2}>COMMUNITY</Text>
            {width >= 600 && <Text style={styles.introSub} numberOfLines={1}>Talk lines, share bets, compare notes. Re-run any shared bet for today's price.</Text>}
          </View>
          {user && (
            <View style={styles.onlinePill} accessible accessibilityLabel={`${onlineCount} members online`}>
              <View style={styles.onlineDot} {...(IS_WEB ? { dataSet: { onlineDot: "" } } : {})} />
              <Text style={styles.onlineText}>{onlineCount} online</Text>
            </View>
          )}
        </Appear>

        {!user ? (
          <Appear index={1} style={styles.gate}>
            <AmbientGlow />
            <EdgeLine height={120} opacity={0.8} style={{ position: "absolute", left: 0, right: 0, top: 30 }} />
            <KineticHeadline
              lines={width < 600 ? ["The room", "where the edge", "gets shared"] : ["The room where", "the edge gets shared"]}
              outline={[width < 600 ? 2 : 1]}
              textStyle={[styles.gateTitle, width >= 1100 && { fontSize: 64, lineHeight: 68 }, width < 600 && { fontSize: 26, lineHeight: 30 }]}
            />
            <Text style={styles.gateBody}>Sign in to read the channels, post a bet from your slip, and see what other members are finding.</Text>
            <View style={{ flexDirection: "row", gap: 10, marginTop: 20, flexWrap: "wrap", justifyContent: "center" }}>
              <MagneticButton label="Sign in" onPress={() => navigateTo("Login", { redirectAfterLogin: "Community" })} style={{ minWidth: 150 }} />
              <MagneticButton label="Create a free account" variant="ghost" arrow={false} onPress={() => navigateTo("Register")} />
            </View>
            {/* Perks only when there's room: the panel is a fixed-height viewport. */}
            {width >= 600 && height >= 760 && (
              <View style={styles.perks}>
                {PERKS.map(([icon, title, body], i) => (
                  <Reveal key={title} index={i + 2} style={{ flex: 1 }}>
                    <Spotlight style={styles.perk} tilt glow="rgba(52,211,153,0.14)">
                      <View style={styles.perkIcon} {...(IS_WEB ? { dataSet: { perkIcon: "" } } : {})}>
                        <Ionicons name={icon} size={20} color="#34D399" accessibilityElementsHidden />
                      </View>
                      <Text style={styles.perkTitle}>{title}</Text>
                      <Text style={styles.perkBody}>{body}</Text>
                      {IS_WEB && <View style={styles.perkLine} dataSet={{ perkLine: "" }} aria-hidden />}
                    </Spotlight>
                  </Reveal>
                ))}
              </View>
            )}
          </Appear>
        ) : (
          <View style={styles.columns}>
            {twoCol && channels}
            <ChannelContent
              communityId={COMMUNITY_ID}
              categoryId={activeCategoryId}
              channelId={activeChannelId}
              channelName={activeChannelName}
              channelTopic={activeChannel?.description || activeChannel?.topic || null}
              compact={!twoCol}
              onOpenChannels={!twoCol ? () => setChannelsOpen(true) : undefined}
              onOpenMembers={!threeCol ? () => setMembersOpen(true) : undefined}
              onlineCount={onlineCount}
            />
            {threeCol && memberList}
          </View>
        )}
      </View>

      <SlideOver open={channelsOpen && !twoCol} side="left" onClose={() => setChannelsOpen(false)} label="Channels">{channels}</SlideOver>
      <SlideOver open={membersOpen && !threeCol} side="right" onClose={() => setMembersOpen(false)} label="Members">{memberList}</SlideOver>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: "#000000",
    height: Platform.OS === "web" ? "100vh" : "100%",
    maxHeight: Platform.OS === "web" ? "100vh" : "100%",
    overflow: "hidden",
  },
  body: { flex: 1, paddingBottom: 12, gap: 10, overflow: "hidden" },
  intro: { flexDirection: "row", alignItems: "center", gap: 14, paddingHorizontal: 14, paddingVertical: 14, borderRadius: 14, borderWidth: 1, borderColor: "rgba(255,255,255,0.08)", backgroundColor: "rgba(255,255,255,0.03)", overflow: "hidden" },
  introLine: { position: "absolute", left: 0, right: 0, bottom: -6 },
  introTitle: { color: "rgba(255,255,255,0.94)", fontFamily: "ClimateCrisis", fontSize: 24, letterSpacing: 0.5 },
  introSub: { color: "rgba(255,255,255,0.6)", fontFamily: UI, fontSize: 13, marginTop: 2 },
  onlinePill: { flexDirection: "row", alignItems: "center", gap: 6, paddingHorizontal: 10, paddingVertical: 5, borderRadius: 999, borderWidth: 1, borderColor: "rgba(52,211,153,0.4)", backgroundColor: "rgba(52,211,153,0.08)" },
  onlineDot: { width: 7, height: 7, borderRadius: 4, backgroundColor: "#34D399" },
  onlineText: { color: "#34D399", fontFamily: UI, fontSize: 12, fontWeight: "700" },
  columns: { flex: 1, flexDirection: "row", gap: 10, overflow: "hidden" },
  overlay: { flex: 1, backgroundColor: "rgba(0,0,0,0.6)" },
  sheet: { width: "86%", maxWidth: 320, height: "100%", backgroundColor: "#0d0d14", borderRightWidth: 1, borderLeftWidth: 1, borderColor: "rgba(255,255,255,0.08)" },
  gate: { flex: 1, alignItems: "center", justifyContent: "center", paddingHorizontal: 16, borderRadius: 18, borderWidth: 1, borderColor: "rgba(255,255,255,0.08)", backgroundColor: "rgba(255,255,255,0.02)", overflow: "hidden" },
  gateTitle: { color: "rgba(255,255,255,0.94)", fontFamily: "ClimateCrisis", fontSize: 44, lineHeight: 48, textAlign: "center", textTransform: "uppercase" },
  gateBody: { color: "rgba(255,255,255,0.65)", fontFamily: UI, fontSize: 16, lineHeight: 24, textAlign: "center", marginTop: 14, maxWidth: 520 },
  perks: { flexDirection: "row", gap: 12, marginTop: 40, width: "100%", maxWidth: 980 },
  perk: { flex: 1, gap: 6, padding: 20, borderRadius: 16, borderWidth: 1, borderColor: "rgba(255,255,255,0.08)", backgroundColor: "rgba(255,255,255,0.025)", overflow: "hidden" },
  perkIcon: { width: 40, height: 40, borderRadius: 12, alignItems: "center", justifyContent: "center", backgroundColor: "rgba(52,211,153,0.08)" },
  perkLine: { position: "absolute", left: 0, right: 0, bottom: 0, height: 2, backgroundColor: "#34D399", ...(Platform.OS === "web" ? { backgroundImage: "linear-gradient(90deg, #6366F1, #8B5CF6, #34D399)" } : {}) },
  perkTitle: { color: "rgba(255,255,255,0.94)", fontFamily: UI, fontSize: 15, fontWeight: "800", marginTop: 4 },
  perkBody: { color: "rgba(255,255,255,0.62)", fontFamily: UI, fontSize: 13, lineHeight: 19 },
});
